'use client';

import Recaptcha from '@/components/Recaptcha';
import { startTokenRefresh } from '@/lib/auth';
import { setCookie } from '@/services/helperFunctions/serverHelperFunctions';
import { makeNavigationSiteAnalytics, makeSiteAnalytics } from '@/services/simpleApis';
import { faArrowLeft, faArrowRight, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { FormEvent, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Swal from 'sweetalert2';
import * as Yup from 'yup';
import * as authenticateAction from '../../redux/actions/authenticate';
import { EXIST_LOCAL_STORAGE } from '../../services/constants';
import { getStorage, removeStorageCookie, setStorage } from '../../services/helperFunctions';
import { getCookie } from '@/services/helperFunctions';
import { LoginForm } from '../../types/login';

const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().required('Password is required'),
});

function LoginPageComponents({ getAuthenticateLogin, resendActivationEmail, deviceLogOut }: any) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const goToAfterLogin = searchParams.get('redirectTo') || 'screener';

    const [loginFormObj, setloginFormObj] = useState<LoginForm>({
        email: '',
        password: '',
        isStayLogin: true,
    });

    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const isRecaptchaVerifiedRef = useRef(false);

    const eye = <FontAwesomeIcon icon={faEye} />;
    const eyeSlash = <FontAwesomeIcon icon={faEyeSlash} />;
    const [passwordShown, setPasswordShown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const handleLoginFormChange = (event: any) => {
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;
        setloginFormObj({
            ...loginFormObj,
            [name]: value,
        });
    };

    const togglePasswordVisiblity = () => setPasswordShown((prev) => !prev);

    const filterData = {
        issuer: '',
        issue_type: 'All bonds',
        user_id: '',
        description: '',
        country: '',
        currency: '',
        region: '',
        industry: '',
        coupon_from: 0,
        coupon_to: 30,
        yield_from: 0,
        yield_to: 30,
        maturity_from_year: new Date().getFullYear(),
        maturity_to_year: new Date().getFullYear() + 100,
        callable: false,
        status: false,
        page_no: 1,
        per_page: 25,
        sorting_column_sorting_order: 'bond_name asc',
    };

    const handleLoginFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleLogin();
    };

    const [showCaptchaRequiredWarning, setShowCaptchaRequiredWarning] = useState(false);

    const redirectAfterLogin = () => {
        try {
            console.log('Clearing cache...');
            if ('serviceWorker' in navigator) {
                caches.keys().then((names) => {
                    names.forEach((name) => {
                        caches.delete(name);
                    });
                });
            }
        } finally {
            console.log(`Navigating to ${goToAfterLogin}`);
            router.push(`/${goToAfterLogin}`);
        }
    };

    const handleLogin = async () => {
        removeStorageCookie();
        setLoading(true);
        try {
            await validationSchema.validate(loginFormObj, { abortEarly: false });

            if (!isRecaptchaVerifiedRef.current) {
                const recaptchaResponse = await fetch('/nextapi/captcha/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ recaptchaToken }),
                });

                if (!recaptchaResponse.ok) {
                    setLoading(false);
                    setShowCaptchaRequiredWarning(true);
                    toast.error('🔒 CAPTCHA verification failed. Please complete the challenge to proceed.');
                    return;
                }
            }

            isRecaptchaVerifiedRef.current = true;
            setShowCaptchaRequiredWarning(false);
            getAuthenticateLogin(loginFormObj).then(async (response: any) => {
                setLoading(false);
                if (response.code === 409) {
                    Swal.fire({
                        // icon: 'warning',
                        text: 'Your email is not verified yet. Do you want to resend confirmation email?',
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        cancelButtonColor: '#d33',
                        confirmButtonText: 'Yes, resend it!',
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            resendActivationEmail({ email: loginFormObj.email });
                        }
                    });
                } else if (response.code === 200) {
                    setErrors({});

                    const { data } = response;

                    //cookies
                    const expireIn = loginFormObj.isStayLogin ? 7 : null;
                    console.log('expireIn', expireIn);

                    setCookie(EXIST_LOCAL_STORAGE.API_KEY, data?.apiKey, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.AUTHTOKEN, data?.token, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.REFRESH_TOKEN, data?.refreshToken, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.EXPIRATION_TIME, data?.expiration, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.USER_ID, data?.userId, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.ROLE, data?.role, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.USERNAME, data?.userName, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.FULL_NAME, data?.fullName, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.USER_IMG, data?.avatar, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.ISSTAYLOGIN, loginFormObj.isStayLogin, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, loginFormObj.isStayLogin, expireIn);
                    setCookie(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, JSON.stringify({ ...filterData, user_id: data?.userId }), expireIn);

                    setStorage(EXIST_LOCAL_STORAGE.ISSTAYLOGIN, JSON.stringify(loginFormObj.isStayLogin));
                    setStorage(EXIST_LOCAL_STORAGE.AUTHTOKEN, data?.token);
                    setStorage(EXIST_LOCAL_STORAGE.REFRESH_TOKEN, data?.refreshToken);
                    setStorage(EXIST_LOCAL_STORAGE.EXPIRATION_TIME, data?.expiration);
                    setStorage(EXIST_LOCAL_STORAGE.API_KEY, data?.apiKey);
                    setStorage(EXIST_LOCAL_STORAGE.FULL_NAME, data?.fullName);
                    setStorage(EXIST_LOCAL_STORAGE.USERNAME, data?.userName);
                    setStorage(EXIST_LOCAL_STORAGE.USER_IMG, data?.avatar);
                    setStorage(EXIST_LOCAL_STORAGE.USER_ID, data?.userId);
                    setStorage(EXIST_LOCAL_STORAGE.ROLE, data?.role);
                    setStorage(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, JSON.stringify({ ...filterData, user_id: data?.userId }));

                    startTokenRefresh();
                    void makeNavigationSiteAnalytics('Login button', '/screener');
                    redirectAfterLogin();
                } else if (response?.message === 'This email already logged.') {
                    Swal.fire({
                        // text: '',
                        text: 'You are already logged in on another device. Do you want to logout on that device?',
                        // icon: 'warning',
                        position: 'top',
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        cancelButtonColor: '#d33',
                        confirmButtonText: 'Yes, logout!',
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            await deviceLogOut(loginFormObj);
                            handleLoginNew();
                        }
                    });
                } else {
                    toast.error(response.message);
                }
            });
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    if (err.path) {
                        newErrors[err.path] = err.message;
                    }
                });
                setErrors(newErrors);
            }
            console.log('Error on login', error);
        } finally {
            setLoading(false);
        }
    };
    const handleLoginNew = () => {
        removeStorageCookie();
        getAuthenticateLogin(loginFormObj).then(async (response: any) => {
            setLoading(false);
            if (response.code === 409) {
                // handle resend confirmation
                Swal.fire({
                    icon: 'warning',
                    text: 'Your email is not verified yet. Do you want to resend confirmation email?',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, resend it!',
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        resendActivationEmail({ email: loginFormObj.email });
                    }
                });
            } else if (response.code === 200) {
                setStorage(EXIST_LOCAL_STORAGE.ISSTAYLOGIN, JSON.stringify(loginFormObj.isStayLogin));
                let { data } = response;
                setErrors({});
                console.log('login data ------------>', data);

                //cookies
                const expireIn = loginFormObj.isStayLogin ? 7 : null;
                console.log('expireIn', expireIn);
                setCookie(EXIST_LOCAL_STORAGE.API_KEY, data?.apiKey, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.AUTHTOKEN, data?.token, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.REFRESH_TOKEN, data?.refreshToken, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.EXPIRATION_TIME, data?.expiration, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.USER_ID, data?.userId, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.ROLE, data?.role, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.USERNAME, data?.userName, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.FULL_NAME, data?.fullName, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.USER_IMG, data?.avatar, expireIn);
                setCookie(EXIST_LOCAL_STORAGE.ISSTAYLOGIN, JSON.stringify(loginFormObj.isStayLogin), expireIn);
                setCookie(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, JSON.stringify({ ...filterData, user_id: data?.userId }), expireIn);

                // Local storage
                setStorage(EXIST_LOCAL_STORAGE.AUTHTOKEN, data?.token);
                setStorage(EXIST_LOCAL_STORAGE.REFRESH_TOKEN, data?.refreshToken);
                setStorage(EXIST_LOCAL_STORAGE.EXPIRATION_TIME, data?.expiration);
                setStorage(EXIST_LOCAL_STORAGE.API_KEY, data?.apiKey);
                setStorage(EXIST_LOCAL_STORAGE.FULL_NAME, data?.fullName);
                setStorage(EXIST_LOCAL_STORAGE.USER_IMG, data?.avatar);
                setStorage(EXIST_LOCAL_STORAGE.USER_ID, data?.userId);
                setStorage(EXIST_LOCAL_STORAGE.ROLE, data?.role);
                setStorage(EXIST_LOCAL_STORAGE.USERNAME, data?.userName);
                setStorage(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, JSON.stringify({ ...filterData, user_id: data?.userId }));

                startTokenRefresh();
                const body = {
                    site_visitor_id: getStorage(EXIST_LOCAL_STORAGE.SITE_VISITOR_ID),
                    user_id: data?.userId,
                };
                makeSiteAnalytics(body);
                makeNavigationSiteAnalytics('Login button', '/screener');
                redirectAfterLogin();
            }
        });
    };

    useEffect(() => {
        const userId = getCookie(EXIST_LOCAL_STORAGE.USER_ID);
        console.log('[LoginPageComponents] userId', userId);

        if (!userId) return;
        redirectAfterLogin();
    }, []);
    return (
        <div className="container ">
            <div className="row">
                <div className="col-sm-12 ">
                    <div className="Wrapper">
                        <div className="loginpage">
                            <form className="needs-validation " autoComplete="new-password" onSubmit={handleLoginFormSubmit}>
                                <div className="form-group mb-2">
                                    <input
                                        type="email"
                                        value={loginFormObj.email}
                                        name="email"
                                        onChange={handleLoginFormChange}
                                        className="form-control text-sm login-input-field-cus"
                                        placeholder="Email"
                                        autoComplete="new-password"
                                        onFocus={(e) => e.target.value}
                                        onBlur={(e) => (e.target.value = loginFormObj.email)}
                                        required
                                    ></input>
                                    {errors.email && <div className="text-danger text-sm">{errors.email}</div>}
                                </div>
                                {/* <div className='form-group was-validated mb-2'> */}
                                <div className="form-group mb-2 pt-4">
                                    {/* <h6 className="form-label pe-5">Password</h6> */}
                                    <div className="input-group">
                                        <input
                                            type={passwordShown ? 'text' : 'password'}
                                            value={loginFormObj.password}
                                            name="password"
                                            onChange={handleLoginFormChange}
                                            className="form-control text-sm  login-input-field-cus"
                                            autoComplete="new-password"
                                            placeholder="Password"
                                            aria-label="Sizing example input"
                                            aria-describedby="inputGroup-sizing-default"
                                            required
                                        ></input>
                                        {passwordShown ? (
                                            <i className="input-group-text" onClick={togglePasswordVisiblity}>
                                                {eye}
                                            </i>
                                        ) : (
                                            <i className="input-group-text" onClick={togglePasswordVisiblity}>
                                                {eyeSlash}
                                            </i>
                                        )}
                                    </div>
                                    {errors.password && <div className="text-danger text-sm">{errors.password}</div>}
                                </div>
                                <div className="form-group my-2 pt-2">
                                    <div className="input-group">
                                        <label htmlFor="isStayLogin" className="text-secondary  text-sm">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="isStayLogin"
                                                id="isStayLogin"
                                                checked={loginFormObj.isStayLogin}
                                                onChange={handleLoginFormChange}
                                            />{' '}
                                            Stay signed in
                                        </label>
                                    </div>
                                </div>
                                <Recaptcha onChange={setRecaptchaToken} showWarning={showCaptchaRequiredWarning} />
                                <button
                                    type="submit"
                                    className="btn bg-shibui-btn text-white mt-4 w-100"
                                    onClick={handleLogin}
                                    disabled={loading}
                                    style={{ height: '48px', marginTop: '0px' }}
                                >
                                    {loading ? 'LOGGING IN...' : 'LOG IN'}
                                </button>
                            </form>
                            <form className="mt-3 d-flex align-items-center justify-content-center w-100">
                                <label className="text-muted  text-sm">
                                    <Link prefetch href="/forgot-password">
                                        Forgot password?
                                    </Link>
                                </label>
                            </form>
                            {/* {isMobile ? (
                <div>
                  <div id="login_reg_separator" className="login_divider both_seperator" data-sigil="login_reg_separator">
                    <span className="or_span">or</span>
                  </div>
                </div>
              ) : (
                <div>

                </div>
              )} */}

                            <form className="mt-3 d-flex align-items-center justify-content-center w-100">
                                <label className="text-muted">
                                    <Link
                                        href="/registerpage"
                                        className="btn btn-success"
                                        style={{ height: '48px', display: 'table-cell' }}
                                    >
                                        Create New Account
                                    </Link>
                                </label>
                            </form>

                            {/* <div className="mt-3 d-flex justify-content-center">
                                    <GoogleOAuthProvider clientId="1032944916740-euh6s44pvmgpu5mjvtebis4u0jfmd2lp.apps.googleusercontent.com">
                                        <GoogleLogin
                                            size='large'
                                            shape='circle'
                                            // text="signup_with"
                                            onSuccess={credentialResponse => {
                                                console.log(credentialResponse,'decode');
                                                const decoded = jwtDecode(credentialResponse?.credential);
                                                
                                                handleLoginOauth(decoded)
                                            }}
                                            onError={() => {
                                                console.log('Login Failed');
                                            }}
                                        />
                                    </GoogleOAuthProvider>
                                </div> */}

                            <form className="mt-3 d-flex align-items-center justify-content-evenly w-100">
                                <label className="text-muted ">
                                    <Link href="/" className="text-sm d-flex justify-content-center align-items-center gap-2">
                                        <FontAwesomeIcon
                                            icon={faArrowLeft}
                                            size="xs"
                                            className="border border-1 border-primary p-1 rounded-circle"
                                        />
                                        <span>Back to home</span>
                                    </Link>
                                </label>
                                <label className="text-muted">
                                    <Link
                                        prefetch
                                        href="/screener"
                                        className="  text-sm d-flex justify-content-center align-items-center gap-2"
                                    >
                                        <span>Go to screener</span>
                                        <FontAwesomeIcon
                                            icon={faArrowRight}
                                            size="xs"
                                            className="border border-1 border-primary p-1 rounded-circle"
                                        />
                                    </Link>
                                </label>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStatesToProps = ({ authenticate: { isloginLoader = false } }: any) => {
    return { isloginLoader };
};

const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators(
        {
            ...authenticateAction,
        },
        dispatch
    );
};

export default connect(mapStatesToProps, mapDispatchToProps)(LoginPageComponents);
